import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../core/services/toast.service';

import {
  LIST_PAGE_CONFIG,
  ListPageConfig,
  ListColumnConfig,
} from 'app/main/components/list-page-config/list-page.config';

import {
  ListCardItem,
  ListCardComponent,
} from 'app/main/components/list-card/list-card.component';

import { OrganizationService } from '../../../../core/services/organization/organization.service';
import {
  Organization,
  OrganizationListData,
} from '../../../../core/models/organization/organization.model';

import { ModalService } from '../../../shared/services/modal.service';
import { CreateOrganizationComponent } from '../create-organization/create-organization.component';
import { EditOrganizationComponent } from '../edit-organization/edit-organization.component';
import { DynamicListConfig } from 'app/core/models/table/dynamic-list.model';
import { DynamicListComponent } from 'app/shared/components/dynamic-list/dynamic-list.component';
import { PageSizeSelectorComponent } from 'app/shared/components/page-size-selector/page-size-selector.component';
import { BaseListComponent } from '../../../../shared/base/base-list.component';
import { ListStateStackService } from '../../../../core/services/list/list-state-stack.service';

@Component({
  selector: 'app-organization-list-demo',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    DynamicListComponent,
    PageSizeSelectorComponent,
  ],
  templateUrl: './organizations.component.html',
  styleUrl: './organizations.component.scss',
  providers: [DatePipe],
})
export class OrganizationsComponent extends BaseListComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private organizationService: OrganizationService,
    private translate: TranslateService,
    private toastService: ToastService,
    private modalService: ModalService,
    listStateStack: ListStateStackService
  ) {
    super(listStateStack, 'organizations');
    this.collapseElementId = 'collapseOrg'; // Set the collapse element ID for this list page
    const userLang = localStorage.getItem('app-lang') || 'en-US';
    this.translate.use(userLang);
  }

  orgConfig: DynamicListConfig = {
    pageTitleKey: 'Organizations',
    columns: [
      {
        key: 'name',
        labelKey: 'NAME',
        type: 'user',
        width: 'minmax(280px, 2.5fr)',
      },
      {
        key: 'customerCode',
        labelKey: 'CUSTOMER CODE',
        type: 'text',
        width: 'minmax(120px, 1fr)',
      },
      {
        key: 'contactPerson',
        labelKey: 'CONTACT PERSON',
        type: 'text',
        width: 'minmax(150px, 1.2fr)',
      },
      {
        key: 'address',
        labelKey: 'ADDRESS',
        type: 'text',
        width: 'minmax(150px, 1.5fr)',
      },
      {
        key: 'email',
        labelKey: 'EMAIL',
        type: 'text',
        width: 'minmax(180px, 1.5fr)',
      },

      {
        key: 'actions',
        labelKey: 'ACTIONS',
        type: 'actions',
        width: 'minmax(140px, 0.8fr)',
      },
    ],
    actions: [
      {
        icon: 'fa-regular fa-pen-to-square',
        title: 'edit',
        onClick: (item) => this.onEdit(item),
      },
      {
        icon: 'fa-regular fa-trash-can',
        title: 'delete',
        onClick: (item) => this.onDeleteCard(item),
      },
    ],
  };

  organizations: Organization[] = [];
  orgCards: ListCardItem[] = [];

  totalItems = 0;
  totalPage = 0;
  pageSizes = [10, 15, 25, 50, 100];
  pageHeight!: number;

  isLoading = true;
  selectedOrgId: string | null = null;

  override filterForm = {
    name: '',
    customerCode: '',
    email: '',
  };

  @ViewChild('openDeleteModalBtn')
  openDeleteModalBtn!: ElementRef<HTMLButtonElement>;



  activeTooltip: { text: string; x: number; y: number } | null = null;
  private tooltipTimeout: any = null;
  private pendingTooltipPosition: { x: number; y: number } | null = null;

  showTooltip(event: MouseEvent, text: string) {
    if (!text) return;
    const translatedText = this.translate.instant(text);

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    const x = rect.left;
    const y = rect.bottom + 4;

    this.pendingTooltipPosition = { x, y };

    if (this.tooltipTimeout) clearTimeout(this.tooltipTimeout);
    this.tooltipTimeout = setTimeout(() => {
      if (this.pendingTooltipPosition) {
        this.activeTooltip = {
          text: translatedText,
          x: this.pendingTooltipPosition.x,
          y: this.pendingTooltipPosition.y,
        };
      }
    }, 300);
  }

  hideTooltip() {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
      this.tooltipTimeout = null;
    }
    this.pendingTooltipPosition = null;
    this.activeTooltip = null;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    // Only load data if state wasn't restored
    if (!this.stateWasRestored) {
      this.getList(this.currentPage, this.pageSize);
    }
  }

  openCreateModal(): void {
    const modalRef = this.modalService.openComponentModal(
      CreateOrganizationComponent,
      {
        size: 'lg',
        backdrop: true,
        keyboard: true,
      }
    );

    modalRef.closed.subscribe((result) => {
      if (result === 'success') {
        this.getList(this.currentPage, this.pageSize, this.filterForm);
      }
    });
  }

  onEdit(card: ListCardItem): void {
    const modalRef = this.modalService.openComponentModal(
      EditOrganizationComponent,
      {
        size: 'lg',
        backdrop: true,
        keyboard: true,
        data: { organizationId: String(card.id) },
      }
    );

    modalRef.closed.subscribe((result) => {
      if (result === 'success') {
        this.getList(this.currentPage, this.pageSize, this.filterForm);
      }
    });
  }



  getList(page: number, size: number, filters?: any): void {
    this.isLoading = true;
    const apiIndex = page - 1;

    const filterName = filters?.name || '';
    const filterCode = filters?.customerCode || '';
    const filterEmail = filters?.email || '';

    this.organizationService
      .getListOrganization(
        apiIndex,
        size,
        filterName,
        filterCode,
        filterEmail,
        this.sortBy,
        this.sortDirection
      )
      .subscribe({
        next: (res) => {
          const data: OrganizationListData = res.data;

          if (data) {
            this.organizations = data.items;

            if (this.sortBy && this.sortDirection) {
              const direction = this.sortDirection === 'asc' ? 1 : -1;
              this.organizations.sort((a, b) => {
                let valA, valB;
                switch (this.sortBy) {
                  case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                  default:
                    return 0;
                }
                if (valA < valB) return -1 * direction;
                if (valA > valB) return 1 * direction;
                return 0;
              });
            }

            this.orgCards = this.organizations.map((org) =>
              this.mapToListCardItem(org)
            );

            this.loadAvatarsForOrganizations();

            this.totalItems = data.count;
            this.totalPage = data.pages;
            this.currentPage = page;
          }

          this.isLoading = false;
        },
        error: (err) => {
          console.error('getOrganizationList error', err);
          this.isLoading = false;
        },
      });
  }

  private mapToListCardItem(org: Organization): ListCardItem {
    return {
      id: org.id,
      name: org.name,
      organization: org.contactPerson || '',
      role: org.customerCode || '',
      email: org.email || '',
      lastLoginDate: org.createdDate || '',
    };
  }

  private loadAvatarsForOrganizations(): void {
    this.organizations.forEach((org, index) => {
      if (org.id && org.avatarFolderPath) {
        this.organizationService.getAvatar(org.id).subscribe({
          next: (res) => {
            if (
              res.isSuccess &&
              res.data?.avatar &&
              this.organizations[index]
            ) {
              this.compressImage(res.data.avatar, (compressedBase64) => {
                this.organizations[index].avatarFolderPath =
                  'data:image/png;base64,' + compressedBase64;
              });
            }
          },
          error: (err) => {
            console.debug('Failed to load avatar for organization:', org.id);
          },
        });
      }
    });
  }

  private compressImage(
    base64String: string,
    callback: (compressedBase64: string) => void
  ): void {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const maxSize = 200; // Max dimension in pixels
      let width = img.width;
      let height = img.height;

      // Scale proportionally to fit within 200px
      if (width > height) {
        if (width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL('image/png').split(',')[1];
        callback(compressedBase64);
      }
    };
    img.src = 'data:image/png;base64,' + base64String;
  }

  filterList(e: Event) {
    e.preventDefault();
    this.getList(1, this.pageSize, this.filterForm);
  }

  clearFilters(): void {
    this.filterForm = { name: '', customerCode: '', email: '' };
    this.sortBy = null;
    this.sortDirection = null;
    this.getList(this.currentPage, this.pageSize);
  }

  changePage(page: number): void {
    this.currentPage = page + 1;

    if (this.currentPage > 0 && this.currentPage <= this.totalPage) {
      this.getList(this.currentPage, this.pageSize, this.filterForm);
    }
  }

  previousPage(page: number) {
    if (this.currentPage > 1) {
      this.getList(this.currentPage - 1, this.pageSize, this.filterForm);
    }
  }

  nextPage(page: number) {
    if (this.currentPage < this.totalPage) {
      this.getList(this.currentPage + 1, this.pageSize, this.filterForm);
    }
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getList(this.currentPage, this.pageSize, this.filterForm);
  }

  onCardClick(card: ListCardItem) {
    this.router.navigate(['/organization/detail', card.id]);
  }

  onDeleteCard(card: ListCardItem) {
    this.selectedOrgId = String(card.id);
    this.openDeleteModalBtn.nativeElement.click();
  }

  confirmDelete() {
    if (!this.selectedOrgId) return;

    this.organizationService.deleteOrganization(this.selectedOrgId).subscribe({
      next: (res) => {
        this.toastService.showToast(
          this.translate.instant('organization.deleteSuccess'),
          'success'
        );
        this.getList(this.currentPage, this.pageSize);
        this.selectedOrgId = null;
      },
    });
  }

  getColClass(col: ListColumnConfig): string {
    switch (col.colType) {
      case 'user':
        return 'col-user';
      case 'dataOrg':
        return 'col-data-org';
      case 'dataEmail':
        return 'col-data-email';
      case 'actions':
        return 'col-actions';
      default:
        return 'col-data';
    }
  }

  onSort(column: string): void {
    if (this.sortBy === column) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortBy = null;
        this.sortDirection = null;
      }
    } else {
      this.sortBy = column;
      this.sortDirection = 'asc';
    }
    this.getList(this.currentPage, this.pageSize, this.filterForm);
  }

  override loadData(): void {
    this.getList(this.currentPage, this.pageSize, this.filterForm);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterViewInit(): void {
    // Restore the filter collapse state to prevent flickering on tablet back navigation
    this.restoreCollapseState();
    // Listen for collapse toggle events
    this.setupCollapseListener();
  }

  private setupCollapseListener(): void {
    if (!this.collapseElementId) return;
    const collapseElement = document.getElementById(this.collapseElementId);
    if (collapseElement) {
      collapseElement.addEventListener('show.bs.collapse', () => this.updateCollapseState(true));
      collapseElement.addEventListener('hide.bs.collapse', () => this.updateCollapseState(false));
    }
  }
}
